/**
 * 車両カードの容量ツールチップ。
 *
 * 数値とバーは表示項目で選んだ数量・重量・体積のうち「最後に選んだ 1 項目」しか
 * 描けない（バーは 1 本しか無い）。残りの項目を確かめる手段が画面に無かったため、
 * hover で選択中の項目をまとめて出す（log_web_issues#295）。
 *
 * 判定と書式を VehicleInfoView に閉じ込めず関数へ出しているのは、行の組み立てだけを
 * 単体で固定したいため（tooltip の DOM を通すと桁や並びの回帰が読みにくい）。
 */
import { loadWeightValueText, quantityValueText, volumeValueText } from './metricText';
import { ValueMetric } from './unassignedDisplay/displayMetricKeys';
import { CapacityLike, LoadTotals } from './vehicleLoad';

export type CapacityTooltipRow = {
  key: ValueMetric;
  label: string;
  /** `99cs / 100cs`。最大が無ければ `99cs / --` */
  valueText: string;
  /** `99%`。最大が不明 (null) か 0 なら空文字 */
  percentText: string;
};

/**
 * 表示項目の並びと単位・書式。並びをここで固定するのは、選択順で組むとトグルの
 * 押し順で行が入れ替わり、車両ごとに読み比べられなくなるため。
 */
const METRIC_SPECS: {
  key: ValueMetric;
  label: string;
  unit: string;
  current: (load: LoadTotals) => number;
  max: (capacity: CapacityLike) => number | null;
  format: (value: number) => string;
}[] = [
  {
    key: 'quantity',
    label: '数量',
    unit: 'cs',
    current: (load) => load.cases,
    max: (capacity) => capacity.maxCapacity ?? null,
    format: quantityValueText,
  },
  {
    key: 'weight',
    label: '重量',
    unit: 'kg',
    current: (load) => load.kg,
    max: (capacity) => capacity.maxCapacityKg ?? null,
    format: loadWeightValueText,
  },
  {
    key: 'volume',
    label: '体積',
    unit: 'm³',
    current: (load) => load.m3,
    max: (capacity) => capacity.maxCapacityM3 ?? null,
    format: volumeValueText,
  },
];

/**
 * 選択中の数値指標ごとに「積載量 / 最大積載量」と利用率の行を組む。
 *
 * 選択が 1 つ以下なら空配列を返す（= 呼び出し側はツールチップを出さない）。
 * バーの数値と同じ情報しか出ないため（issue の指定）。
 *
 * 利用率は丸める前の実値から計算する（表示値から計算すると桁を丸めたぶん率がずれる）。
 * 超過していても色は変えない — 超過の通知は表示項目に依らない警告バッジが担う。
 */
export function buildCapacityTooltipRows(
  metrics: readonly ValueMetric[],
  load: LoadTotals,
  capacity: CapacityLike,
): CapacityTooltipRow[] {
  const selected = new Set(metrics);
  if (selected.size <= 1) return [];

  return METRIC_SPECS.filter((spec) => selected.has(spec.key)).map((spec) => {
    const current = spec.current(load);
    const max = spec.max(capacity);
    const known = max != null && max > 0;
    return {
      key: spec.key,
      label: spec.label,
      valueText: `${spec.format(current)}${spec.unit} / ${max != null ? `${spec.format(max)}${spec.unit}` : '--'}`,
      percentText: known ? `${Math.round((current / max) * 100)}%` : '',
    };
  });
}
